import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import appHomeScreen from '../assets/images/hero/app-home-screen.jpg';
import heroHouses from '../assets/images/hero/hero-houses.png';
import heroCloud from '../assets/images/hero/hero-cloud.png';
import AppCta from '../components/AppCta';
import GooglePlayLogo, { PLAY_STORE_URL } from '../components/GooglePlayLogo';
import { homeFeatures } from '../data/features';

const Home: React.FC = () => {
  const fadeRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    fadeRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Scroll to a section when the URL has a hash, e.g. /#features from the header
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const target = document.getElementById(location.hash.slice(1));
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash]);

  const setFadeRef = (index: number) => (el: HTMLDivElement | null) => {
    fadeRefs.current[index] = el;
  };

  const reasons = [
    {
      icon: 'fas fa-mobile-alt',
      title: 'Mobile-First, Built for Landlords',
      description: 'Manage properties, tenants, payments, and maintenance from your phone — anywhere, anytime.'
    },
    {
      icon: 'fas fa-wand-magic-sparkles',
      title: 'AI-Powered Lease Drafting',
      description: 'Generate complete, professional rental agreements in minutes — customize clauses and export as PDF.'
    },
    {
      icon: 'fas fa-file-invoice-dollar',
      title: 'Smart Payment Tracking',
      description: 'Auto-calculated due dates, overdue alerts, PDF receipts with QR codes, and email delivery to tenants.'
    },
    {
      icon: 'fas fa-shield-alt',
      title: 'Secure & Private',
      description: 'Encrypted cloud storage, automatic EXIF removal from photos, and compliant with global data privacy standards.'
    }
  ];

  return (
    <div>
      <SEO
        title="eTENANT: Rental Manager App for Landlords"
        description="eTENANT is a free Android app for landlords and property owners. Manage properties, tenants, and rent, track maintenance, and draft agreements with AI."
        path="/"
        includeAppSchema
      />

      {/* Hero Section */}
      <section className="home-hero">
        <div className="home-hero-bg" aria-hidden="true">
          <span className="home-hero-circle home-hero-circle-1"></span>
          <span className="home-hero-circle home-hero-circle-2"></span>
          <span className="home-hero-glow home-hero-glow-center"></span>
          <span className="home-hero-glow home-hero-glow-right"></span>
        </div>

        <svg className="home-hero-wave home-hero-wave-back" viewBox="0 0 1785 360" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0,38 C17,46 67,72 100,88 C133,104 167,120 200,134 C233,148 267,160 300,171 C333,182 367,192 400,200 C433,208 467,215 500,220 C533,225 567,228 600,231 C633,234 667,236 700,236 C733,236 767,235 800,234 C833,233 858,231 900,228 C942,225 1000,220 1050,214 C1100,208 1150,202 1200,195 C1250,188 1300,178 1350,170 C1400,162 1450,152 1500,144 C1550,136 1602,127 1650,120 C1698,113 1762,103 1785,100 L1785,360 L0,360 Z" />
        </svg>

        <div className="container home-hero-container">
          <div className="home-hero-content">
            <h1 className="home-hero-heading">
              <span className="home-hero-eyebrow">The smarter way to</span>
              <span className="home-hero-title">Manage Your Rentals Easily</span>
            </h1>
            <p className="home-hero-description">
              eTENANT makes it easy to manage properties, collect rent, track maintenance, and handle tenant agreements —<br className="home-hero-br" /> all in one mobile app.
            </p>
            <div className="home-hero-actions">
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hero-btn hero-btn-play"
              >
                <GooglePlayLogo className="hero-btn-play-logo" />
                Download on Google Play
              </a>
              <Link to="/how-it-works" className="hero-btn hero-btn-outline">
                <span className="hero-btn-play-circle" aria-hidden="true">
                  <i className="fas fa-play"></i>
                </span>
                Learn More
              </Link>
            </div>
          </div>

          {/* Phone, houses and cloud are laid out in the design's own pixel space (1320 x 804) and scaled per breakpoint */}
          <div className="home-hero-stage-wrap">
            <div className="home-hero-stage home-hero-stage-back">
              <img className="home-hero-cloud" src={heroCloud} alt="" aria-hidden="true" />
              <img className="home-hero-houses" src={heroHouses} alt="" aria-hidden="true" />
            </div>
            <div className="home-hero-stage home-hero-stage-front">
              <div className="hero-phone-ground" aria-hidden="true"></div>
              <div className="hero-stars" aria-hidden="true">
                <span className="hero-star hero-star-1">★</span>
                <span className="hero-star hero-star-2">✦</span>
                <span className="hero-star hero-star-3">★</span>
                <span className="hero-star hero-star-4">✦</span>
                <span className="hero-star hero-star-5">★</span>
                <span className="hero-star hero-star-6">✦</span>
                <span className="hero-star hero-star-7">★</span>
                <span className="hero-star hero-star-8">✦</span>
              </div>
              <div className="hero-phone-group">
                <div className="hero-phone-shadow" aria-hidden="true"></div>
                <div className="hero-phone-side" aria-hidden="true"></div>
                <div className="hero-phone">
                  <div className="hero-phone-screen">
                    <img src={appHomeScreen} alt="eTENANT app home screen showing unpaid rent, upcoming dues, and monthly payments" />
                  </div>
                  <div className="hero-phone-island" aria-hidden="true"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <svg className="home-hero-wave home-hero-wave-front" viewBox="0 0 1785 360" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0,104 C17,110 67,130 100,142 C133,154 167,163 200,173 C233,183 267,192 300,200 C333,208 367,216 400,222 C433,228 467,234 500,239 C533,244 567,247 600,250 C633,253 667,254 700,255 C733,256 767,256 800,256 C833,256 858,255 900,253 C942,251 1000,248 1050,244 C1100,240 1150,234 1200,228 C1250,222 1300,214 1350,206 C1400,198 1450,190 1500,182 C1550,174 1602,167 1650,160 C1698,153 1762,145 1785,142 L1785,360 L0,360 Z" />
        </svg>
      </section>

      {/* Features Section */}
      <section id="features" className="features-v2">
        <div className="features-v2-blob features-v2-blob-1" aria-hidden="true"></div>
        <div className="features-v2-blob features-v2-blob-2" aria-hidden="true"></div>

        <div className="container position-relative">
          <div ref={setFadeRef(0)} className="features-v2-header fade-in-up">
            <span className="features-v2-eyebrow">Features</span>
            <h2 className="features-v2-title">
              Everything You Need to <span>Manage Rentals</span>
            </h2>
            <p className="features-v2-subtitle">
              All your property management tools in one mobile app — from rent collection to AI-drafted agreements.
            </p>
          </div>

          <div className="row g-4">
            {homeFeatures.map((feature, index) => (
              <div key={feature.title} className="col-xl-4 col-md-6">
                <div ref={setFadeRef(index + 1)} className={`features-v2-card fade-in-up delay-${(index % 3) + 1}`}>
                  <div className="features-v2-card-top">
                    <span className="features-v2-icon">
                      <i className={feature.icon} aria-hidden="true"></i>
                    </span>
                    {feature.badge && <span className="features-v2-badge">{feature.badge}</span>}
                  </div>
                  <h3 className="features-v2-card-title">{feature.title}</h3>
                  <p className="features-v2-card-text">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="features-v2-more">
            <Link to="/features" className="features-v2-more-btn">
              Explore all features
              <i className="fas fa-arrow-right" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <AppCta />

      {/* Why Choose Section */}
      <section className="why-v2">
        <div className="container">
          <div ref={setFadeRef(14)} className="why-v2-header fade-in-up">
            <span className="features-v2-eyebrow">Why choose us</span>
            <h2 className="features-v2-title">
              Why Landlords Choose <span>eTENANT</span>
            </h2>
            <p className="features-v2-subtitle">
              Homes, apartments, condos, commercial spaces, and parking slots — managed from one app built for monthly and long-term rentals.
            </p>
          </div>

          <div ref={setFadeRef(15)} className="why-v2-showcase fade-in-up delay-1">
            <img
              src="/etenant-banner-10232026-v1.png"
              alt="eTENANT app managing homes, apartments, condos, commercial spaces, and parking slots"
              width={1024}
              height={500}
              loading="lazy"
            />
          </div>

          <div className="row g-4">
            {reasons.map((reason, index) => (
              <div key={reason.title} className="col-xl-3 col-md-6">
                <div ref={setFadeRef(16 + index)} className={`why-v2-card fade-in-up delay-${(index % 3) + 1}`}>
                  <div className="why-v2-card-top">
                    <span className="features-v2-icon">
                      <i className={reason.icon} aria-hidden="true"></i>
                    </span>
                  </div>
                  <h3 className="features-v2-card-title">{reason.title}</h3>
                  <p className="features-v2-card-text">{reason.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
